import { Button, Image, Input, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { noop } from '../../../lib/utils';
import { KEYNAMES } from '../../constants';
import styles from './GasPriceEditor.module.css';

const cx = classnames.bind(styles);

/**
 * A component that can be used to edit a single fuel price on both mobile and desktop devices
 *
 * @class GasPriceEditor
 * @extends {React.Component}
 */
export default function GasPriceEditor({
  error,
  inputId,
  isCash,
  isConfirmed: initialIsConfirmed,
  isInDollars,
  onConfirm,
  showBadge,
  value: initialValue,
  ...rest
}) {
  const getInputValue = v => (v ? v.toString() : '');

  const [value, setValue] = useState(getInputValue(initialValue));
  const [templateValue, setTemplateValue] = useState(isInDollars ? '#.##' : '##.#');
  const [isConfirmed, setIsConfirmed] = useState(initialIsConfirmed);
  const [isEditing, setIsEditing] = useState(false);

  const handleBlur = useCallback(() => {
    if (value === '') {
      setValue(getInputValue(initialValue));
      onConfirm(undefined);
    } else {
      setIsConfirmed(true);
      onConfirm(value);
    }

    setIsEditing(false);
  }, [initialValue, onConfirm, value]);

  const handleFocus = useCallback(() => {
    if (value === getInputValue(initialValue) && !isConfirmed) {
      setValue('');
    }

    setIsConfirmed(false);
    setIsEditing(true);
  }, [initialValue, isConfirmed, value]);

  const handleNewTemplateValue = useCallback((updatedTemplateValue) => {
    // ensure we've unconfirmed the price
    if (isConfirmed) {
      onConfirm(undefined);
      setIsConfirmed(false);
    }

    // update our template value state
    setTemplateValue(updatedTemplateValue);

    // update our value state as appropriate to the new template value
    if (updatedTemplateValue.replace('.', '').match(/^#+$/g)) {
      setValue('');
    } else {
      setValue(updatedTemplateValue.replace(/#/g, '0'));
    }
  }, [isConfirmed, onConfirm]);

  const handleDigitKeyDown = useCallback((digitKey) => {
    let newTemplateValue = '';
    // Prices in cents might have 3 digits before the decimal, but those will lead with a 1
    if (!isInDollars && templateValue[0] === '#' && digitKey === '1') {
      newTemplateValue = '1##.#';
    } else {
      newTemplateValue = templateValue.replace('#', digitKey);
    }
    handleNewTemplateValue(newTemplateValue);
  }, [isInDollars, templateValue, handleNewTemplateValue]);

  const handleBackspaceKeyDown = useCallback(() => {
    let newTemplateValue = '';
    // Reset the template as necessary back to expect 2 digits before the decimal for prices in cents
    if (!isInDollars && templateValue[0] === '1' && templateValue[1] === '#') {
      newTemplateValue = '##.#';
    } else {
      let lastInputCharIndex = templateValue.indexOf('#') - 1;
      // If no '#'s are found, then we know to "remove" the last character
      lastInputCharIndex = lastInputCharIndex < -1 ? templateValue.length - 1 : lastInputCharIndex;

      // Skip the decimal place
      if (templateValue.charAt(lastInputCharIndex) === '.') {
        lastInputCharIndex -= 1;
      }

      // If the string was all '#'s, lastInputCharIndex will be -1 and then we don't update anything
      if (lastInputCharIndex <= -1) {
        return;
      }

      // replace the last character input by the user with a '#'
      newTemplateValue = `${templateValue.substring(0, lastInputCharIndex)}#${templateValue.substring(lastInputCharIndex + 1)}`;
    }

    handleNewTemplateValue(newTemplateValue);
  }, [isInDollars, templateValue, handleNewTemplateValue]);

  const handleKeyDown = useCallback((e) => {
    // allow user to still tab through inputs, but otherwise prevent all other default behavior of an input on keydown
    if (e.key !== KEYNAMES.TAB) {
      e.preventDefault();
    }

    // We only want to handle keys pressed that are digits or a Backspace
    if (e.key.match(/\d/)) {
      handleDigitKeyDown(e.key);
    } else if (e.key === KEYNAMES.BACKSPACE) {
      handleBackspaceKeyDown();
    }
  }, [handleBackspaceKeyDown, handleDigitKeyDown]);

  const handleConfirm = useCallback((e) => {
    e.preventDefault();
    setValue(value);
    setIsConfirmed(true);
    onConfirm(value);
  }, [onConfirm, value]);

  useEffect(() => {
    setIsConfirmed(initialIsConfirmed);
  }, [initialIsConfirmed]);

  useEffect(() => {
    setValue(getInputValue(initialValue));
  }, [initialValue]);

  useEffect(() => {
    // This needs a setTimeout so we ensure we set the cursor after the browser has reset its position to the end of the input string
    const typingTimer = setTimeout(() => {
      const inputEl = document.getElementById(inputId);
      if (inputEl && isEditing) {
        const replacedCharacterIndex = templateValue.indexOf('#');
        inputEl.focus();
        inputEl.setSelectionRange(replacedCharacterIndex, replacedCharacterIndex);
      }
    }, 0);
    return () => {
      if (typingTimer) {
        clearTimeout(typingTimer);
      }
    };
  }, [inputId, isEditing, templateValue]);

  return (
    <React.Fragment>
      <div className={cx('gasPriceEditor')}>
        <Input
          {...rest}
          className={cx('priceInput', { confirmed: isConfirmed, hasBadge: showBadge, hasError: !!error })}
          id={inputId}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          value={value}
        />
        <span className={cx('badge', { creditBadge: !isCash, hidden: !showBadge })}>
          {isCash ? 'CASH' : 'CREDIT'}
        </span>
        {isConfirmed && !error && (
          <Image
            alt="Price Confirmed Icon"
            className={cx('priceConfirmedIcon')}
            src="https://static.gasbuddy.com/web/consumer/button-quick-report-activated-green.svg"
          />
        )}
        {!isConfirmed && !isEditing && !!value && (
          <Button link onClick={handleConfirm}>{`Confirm ${value}`}</Button>
        )}
      </div>
      {!!error && (
        <Text as="p" className={cx('errorMessage')} color="orange">{error}</Text>
      )}
    </React.Fragment>
  );
}

GasPriceEditor.propTypes = {
  /** A string used to signify an error for the editor */
  error: PropTypes.string,
  /** A unique string used to identify the input in this component */
  inputId: PropTypes.string.isRequired,
  /** A boolean to determine if the price is cash-only */
  isCash: PropTypes.bool,
  /** A boolean to determine if the price is already confirmed */
  isConfirmed: PropTypes.bool,
  /** A boolean to determine if the prices should be in dollars or cents */
  isInDollars: PropTypes.bool,
  /** A callback executed when a user confirms the fuel price */
  onConfirm: PropTypes.func,
  /** A boolean to determine whether the CASH and CREDIT badge should show */
  showBadge: PropTypes.bool,
  /** The desired input value */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

GasPriceEditor.defaultProps = {
  error: undefined,
  isCash: false,
  isConfirmed: false,
  isInDollars: false,
  onConfirm: noop,
  showBadge: false,
  value: undefined,
};
